/**
 * What depends on a question, an answer, a grid row or measure, or a scale:
 * the one detector (L3). The applier (`apply-operations.ts`) reads it to
 * refuse, prune or report each dependent of what its batch removed; the
 * editor reads it to list what a delete would leave dangling, and clears
 * them with `clearDependents` in the same edit. Detection is pure: it never
 * changes the questions it reads.
 *
 * The input is the questions as they stand after the change, and what the
 * change removed (`QuestionnaireRemovals`). The applier builds the removals
 * from its operations (each one names the operation that removed it); the
 * editor's delete names its one question (`dependentsOfDelete`).
 */
import { conditionOperatorPolarity } from "./condition-operators";
import { isVisibilityRuleGroup } from "./index";

type Json = Record<string, unknown>;

const isRecord = (value: unknown): value is Json =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/** Anything that answers "was this removed?": a Set, or a Map keyed by it. */
export interface RemovedMembers {
  has(member: string): boolean;
}

export interface QuestionnaireRemovals {
  /** Deleted questions by stable key, with their id: a follow-up names its parent by id. */
  questions: ReadonlyMap<string, { id?: unknown }>;
  /** Answer values (`response.options[].value`) that left a question. */
  values: ReadonlyMap<string, RemovedMembers>;
  /**
   * Rows that left a grid: a fixed row by its `value`; on a grid whose rows
   * are the tested assets, a removed asset's id. The applier names both.
   */
  rows: ReadonlyMap<string, RemovedMembers>;
  /** Grid measures (`id`) that left a rating grid. */
  measures: ReadonlyMap<string, RemovedMembers>;
  /** Questions whose answers were rewritten, with their response before. */
  rewritten: ReadonlyMap<string, unknown>;
}

export type QuestionDependentKind =
  /** A condition leaf reads a deleted question. */
  | "condition_source"
  /** A condition leaf names answer values its source no longer has. */
  | "condition_value"
  /** A condition leaf reads a grid row its source no longer has. */
  | "condition_row"
  /** A condition leaf reads a grid measure its source no longer has. */
  | "condition_measure"
  /** A threshold leaf on a source whose scale changed. */
  | "condition_threshold"
  /** An "is not applicable" leaf on a source that no longer offers N/A. */
  | "condition_not_applicable"
  /** A skip lands on a deleted question. */
  | "skip_target"
  /** Carried options come from a deleted question. */
  | "choices_from_source"
  /** A follow-up names a deleted question as its parent. */
  | "follow_up_parent";

export interface QuestionDependent {
  kind: QuestionDependentKind;
  /** The dependent question. */
  questionStableKey: string;
  /** The dependent field, inside that question (`visibilityRule.rules[0].values`). */
  path: string;
  /** The question it depends on. */
  sourceStableKey: string;
  /** A condition dependent's leaf, inside the question (`visibilityRule.rules[0]`). */
  leafPath?: string;
  /** `condition_value`: the removed values the leaf names. */
  values?: Array<string>;
}

/**
 * A condition leaf as stored: `dependsOnStableKey`, `operator` and the fields
 * its operator reads. Loosely typed: drafts reach here from the wire.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ConditionLeafRecord = Record<string, any>;

/**
 * Every leaf of a question's visibility rule, route condition and lane
 * exclusions (`QuestionLaneExclusion`), with its path.
 */
export function conditionLeaves(
  question: unknown,
): Array<{ leaf: ConditionLeafRecord; path: string }> {
  const leaves: Array<{ leaf: ConditionLeafRecord; path: string }> = [];
  if (!isRecord(question)) return leaves;
  const walk = (expr: unknown, path: string) => {
    if (!isRecord(expr)) return;
    if (isVisibilityRuleGroup(expr)) {
      (expr.rules as Array<unknown>).forEach((leaf, index) => {
        if (isRecord(leaf)) leaves.push({ leaf, path: `${path}.rules[${index}]` });
      });
      return;
    }
    leaves.push({ leaf: expr, path });
  };
  walk(question.visibilityRule, "visibilityRule");
  walk(isRecord(question.routingRule) ? question.routingRule.when : undefined, "routingRule.when");
  if (Array.isArray(question.laneExclusions)) {
    question.laneExclusions.forEach((exclusion, index) => {
      if (isRecord(exclusion)) walk(exclusion.when, `laneExclusions[${index}].when`);
    });
  }
  return leaves;
}

/**
 * Whether a visibility leaf left with no value holds for every respondent and
 * may leave its rule unchanged in meaning: an exclusion (its operator's
 * registry polarity) that is the rule's sole leaf or sits in an `and` group.
 * Routing, `or` groups and inclusions never qualify (C07-2).
 */
function alwaysHolds(question: Json, leaf: ConditionLeafRecord, path: string): boolean {
  if (!path.startsWith("visibilityRule")) return false;
  if (conditionOperatorPolarity(leaf?.operator) !== "exclude") return false;
  const rule = question.visibilityRule;
  return isVisibilityRuleGroup(rule) ? rule.join === "and" : rule === leaf;
}

/**
 * The lane exclusion a value-less leaf leaves holding for nobody, so skipping
 * no one: its leaf names answers to include (`any_of`), as the condition's
 * sole leaf or in an `and` group. Dropping it keeps who answers each lane
 * (D-b). Null for any other leaf.
 */
function exclusionSkippingNobody(
  question: Json,
  leaf: ConditionLeafRecord,
  path: string,
): number | null {
  const at = /^laneExclusions\[(\d+)\]\.when/.exec(path);
  if (!at || conditionOperatorPolarity(leaf?.operator) !== "include") return null;
  const index = Number(at[1]);
  const when = (question.laneExclusions as Array<Json> | undefined)?.[index]?.when;
  return (isVisibilityRuleGroup(when) ? when.join === "and" : when === leaf) ? index : null;
}

/**
 * What a condition leaf becomes when the answers `gone` leave its source (a
 * `condition_value` dependent), the one decision for the applier and the
 * editor's live check, as `[survivors, exclusion, refused]`: the values that
 * stay; a lane exclusion that now skips nobody; or `refused`, when no value
 * stays and the leaf is neither of those nor one that holds for everyone. A
 * leaf with no value left that holds for everyone leaves its rule (`survivors`
 * is empty, nothing refused).
 */
export function leafAfterAnswersLeave(
  question: unknown,
  leaf: ConditionLeafRecord,
  leafPath: string,
  gone: RemovedMembers,
): [survivors: Array<string>, exclusion: number | null, refused: boolean] {
  const host = isRecord(question) ? question : {};
  const survivors = (Array.isArray(leaf.values) ? leaf.values : []).filter(
    (value): value is string => typeof value === "string" && !gone.has(value),
  );
  const exclusion = survivors.length === 0 ? exclusionSkippingNobody(host, leaf, leafPath) : null;
  const refused =
    survivors.length === 0 && exclusion === null && !alwaysHolds(host, leaf, leafPath);
  return [survivors, exclusion, refused];
}

/** The grid measure a leaf reads: the named one, or a grid's sole measure. */
function measureOf(response: Json, measureId: unknown): Json | undefined {
  if (!Array.isArray(response.measures)) return undefined;
  const measures = response.measures.filter(isRecord);
  if (typeof measureId === "string") {
    return measures.find((measure) => measure.id === measureId);
  }
  return response.measures.length === 1 ? measures[0] : undefined;
}

/**
 * The scale a numeric condition on this answer is measured on: a rating's
 * unit, a number's unit with its currency and duration, or the unit of the
 * grid measure the leaf reads. The same threshold on another scale is another
 * cut. Null when the answer takes no threshold.
 */
export function thresholdScale(response: unknown, measureId: unknown): string | null {
  if (!isRecord(response)) return null;
  if (response.kind === "rating") return JSON.stringify({ unit: response.unit });
  if (response.kind === "continuous") {
    return JSON.stringify({
      unit: response.unit,
      currencyCode: response.currencyCode ?? null,
      durationUnit: response.durationUnit ?? null,
    });
  }
  if (response.kind !== "matrix_rating") return null;
  const measure = measureOf(response, measureId);
  return measure ? JSON.stringify({ unit: measure.unit }) : null;
}

/** Whether the answer (or the grid measure a leaf reads) offers "not applicable". */
export function hasDeclaredNotApplicable(response: unknown, measureId: unknown): boolean {
  if (!isRecord(response)) return false;
  if (response.kind === "rating") return response.includeNotApplicable === true;
  if (response.kind !== "matrix_rating") return false;
  return measureOf(response, measureId)?.includeNotApplicable === true;
}

/**
 * Every condition, skip, carried list and follow-up among `questions` that
 * names what `removals` removed, in questionnaire order (each question's
 * condition leaves first, then its skip, carried list and follow-ups).
 */
export function dependentsOf(
  questions: ReadonlyArray<unknown>,
  removals: QuestionnaireRemovals,
): Array<QuestionDependent> {
  const dependents: Array<QuestionDependent> = [];
  const deletedById = new Map<string, string>();
  removals.questions.forEach((deleted, stableKey) => {
    if (typeof deleted?.id === "string") deletedById.set(deleted.id, stableKey);
  });
  const responseOf = (stableKey: string): unknown =>
    (questions.find((question) => isRecord(question) && question.stableKey === stableKey) as Json | undefined)
      ?.response;

  for (const question of questions) {
    if (!isRecord(question)) continue;
    const questionStableKey = String(question.stableKey);
    const push = (dependent: Omit<QuestionDependent, "questionStableKey">) =>
      dependents.push({ questionStableKey, ...dependent });

    for (const { leaf, path: leafPath } of conditionLeaves(question)) {
      const source = leaf.dependsOnStableKey;
      if (typeof source !== "string") continue;
      if (removals.questions.has(source)) {
        push({ kind: "condition_source", path: `${leafPath}.dependsOnStableKey`, sourceStableKey: source, leafPath });
        continue;
      }
      const gone = removals.values.get(source);
      if (gone && Array.isArray(leaf.values)) {
        const values = leaf.values.filter(
          (value): value is string => typeof value === "string" && gone.has(value),
        );
        if (values.length > 0) {
          push({ kind: "condition_value", path: `${leafPath}.values`, sourceStableKey: source, leafPath, values });
        }
      }
      if (typeof leaf.matrixRowValue === "string" && removals.rows.get(source)?.has(leaf.matrixRowValue)) {
        push({ kind: "condition_row", path: `${leafPath}.matrixRowValue`, sourceStableKey: source, leafPath });
      }
      if (typeof leaf.matrixMeasureId === "string" && removals.measures.get(source)?.has(leaf.matrixMeasureId)) {
        push({ kind: "condition_measure", path: `${leafPath}.matrixMeasureId`, sourceStableKey: source, leafPath });
      }
      if (!removals.rewritten.has(source)) continue;
      const before = removals.rewritten.get(source);
      const after = responseOf(source);
      if (
        leaf.operator === "is_not_applicable" &&
        hasDeclaredNotApplicable(before, leaf.matrixMeasureId) &&
        !hasDeclaredNotApplicable(after, leaf.matrixMeasureId)
      ) {
        push({ kind: "condition_not_applicable", path: `${leafPath}.operator`, sourceStableKey: source, leafPath });
      }
      if (typeof leaf.threshold === "number") {
        const scaleBefore = thresholdScale(before, leaf.matrixMeasureId);
        const scaleAfter = thresholdScale(after, leaf.matrixMeasureId);
        if (scaleBefore !== null && scaleAfter !== null && scaleBefore !== scaleAfter) {
          push({ kind: "condition_threshold", path: `${leafPath}.threshold`, sourceStableKey: source, leafPath });
        }
      }
    }

    const route = isRecord(question.routingRule) ? question.routingRule : null;
    if (route?.action === "skip_to" && typeof route.targetStableKey === "string" && removals.questions.has(route.targetStableKey)) {
      push({ kind: "skip_target", path: "routingRule.targetStableKey", sourceStableKey: route.targetStableKey });
    }
    const response = isRecord(question.response) ? question.response : {};
    const carried = isRecord(response.choicesFrom) ? response.choicesFrom.sourceStableKey : undefined;
    if (typeof carried === "string" && removals.questions.has(carried)) {
      push({ kind: "choices_from_source", path: "response.choicesFrom", sourceStableKey: carried });
    }
    const parents: Array<unknown> = Array.isArray(response.followUpToQuestionIds) ? response.followUpToQuestionIds : [];
    parents.forEach((parentId, index) => {
      const parent = deletedById.get(String(parentId));
      if (parent !== undefined) {
        push({ kind: "follow_up_parent", path: `response.followUpToQuestionIds[${index}]`, sourceStableKey: parent });
      }
    });
  }
  return dependents;
}

/** Key-order-independent JSON, for "did this response change" or "are these two conditions one". */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

/**
 * What depends on the question `stableKey`, for the editor's delete: the
 * removal is that one question, by its key and id (a follow-up names its
 * parent by id); nothing else leaves.
 */
export function dependentsOfDelete(
  questions: ReadonlyArray<unknown>,
  stableKey: string,
): Array<QuestionDependent> {
  const deleted = questions.find((question) => isRecord(question) && question.stableKey === stableKey);
  const after = questions.filter((question) => question !== deleted);
  return dependentsOf(after, {
    questions: new Map(isRecord(deleted) ? [[stableKey, { id: deleted.id }]] : []),
    values: new Map(),
    rows: new Map(),
    measures: new Map(),
    rewritten: new Map(),
  });
}

/**
 * The questions with each dependent's link cleared: a condition leaf leaves
 * its rule (a rule or route condition left with no leaf goes, with its
 * route, and a lane exclusion with its lane skip); a skip to a deleted
 * question goes; carried options stay as the
 * question's own list, no longer carried; a follow-up drops the deleted
 * parent. Pure: a question with nothing to clear is returned as it was.
 */
export function clearDependents<T>(
  questions: ReadonlyArray<T>,
  dependents: ReadonlyArray<QuestionDependent>,
): Array<T> {
  const byQuestion = new Map<string, Array<QuestionDependent>>();
  dependents.forEach((dependent) => {
    byQuestion.set(dependent.questionStableKey, [...(byQuestion.get(dependent.questionStableKey) ?? []), dependent]);
  });
  return questions.map((question) => {
    if (!isRecord(question)) return question;
    const own = byQuestion.get(String(question.stableKey));
    if (!own) return question;
    const next: Json = { ...question };
    const leafPaths = new Set(own.map((dependent) => dependent.leafPath).filter((path): path is string => !!path));
    const dropped = new Set(
      conditionLeaves(question).filter(({ path }) => leafPaths.has(path)).map(({ leaf }) => leaf),
    );
    const withoutDropped = (expr: unknown): unknown => {
      if (!isRecord(expr)) return expr;
      if (isVisibilityRuleGroup(expr)) {
        const rules = (expr.rules as Array<unknown>).filter((leaf) => !dropped.has(leaf as Json));
        if (rules.length === expr.rules.length) return expr;
        return rules.length > 0 ? { ...expr, rules } : undefined;
      }
      return dropped.has(expr) ? undefined : expr;
    };
    if (dropped.size > 0) {
      const visibilityRule = withoutDropped(question.visibilityRule);
      if (visibilityRule === undefined) delete next.visibilityRule;
      else next.visibilityRule = visibilityRule;
      if (isRecord(question.routingRule)) {
        const when = withoutDropped(question.routingRule.when);
        if (when === undefined) delete next.routingRule;
        else if (when !== question.routingRule.when) next.routingRule = { ...question.routingRule, when };
      }
      if (Array.isArray(question.laneExclusions)) {
        const exclusions = question.laneExclusions.flatMap((exclusion: unknown) => {
          if (!isRecord(exclusion)) return [exclusion];
          const when = withoutDropped(exclusion.when);
          if (when === undefined) return [];
          return [when === exclusion.when ? exclusion : { ...exclusion, when }];
        });
        if (exclusions.length === 0) delete next.laneExclusions;
        else next.laneExclusions = exclusions;
      }
    }
    if (own.some((dependent) => dependent.kind === "skip_target")) delete next.routingRule;
    const response = isRecord(question.response) ? question.response : null;
    const uncarried = own.some((dependent) => dependent.kind === "choices_from_source");
    const parents = new Set(own.filter((dependent) => dependent.kind === "follow_up_parent").map((dependent) => dependent.path));
    if (response && (uncarried || parents.size > 0)) {
      const nextResponse: Json = { ...response };
      if (uncarried) delete nextResponse.choicesFrom;
      if (parents.size > 0 && Array.isArray(response.followUpToQuestionIds)) {
        nextResponse.followUpToQuestionIds = response.followUpToQuestionIds.filter(
          (_parent, index) => !parents.has(`response.followUpToQuestionIds[${index}]`),
        );
      }
      next.response = nextResponse;
    }
    return next as T;
  });
}
